import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
} from '@chakra-ui/react'
import { FieldMapValue } from '@hb/shared'

import { useScreen } from '@hb/react'
import { set as nestedSet } from 'nested-property'
import { FC, PropsWithChildren, useMemo } from 'react'
import { SimpleForm } from '../forms/FinalForm/SimpleForm'
import { DefaultModal } from '../Modals/DefaultModal'
import { Subheader } from '../Users/Text'
import { BaseDataView } from './BaseDataView'
import { FormPopoverProps, GenericEditModalProps } from './types'

export const FormPopover = <Data extends FieldMapValue>({
  data,
  onSubmit,
  children,
  field,
  ...props
}: FormPopoverProps<Data>) => (
  <Popover strategy="fixed" isLazy closeOnBlur={false} returnFocusOnClose {...props}>
    {({ onClose }) => (
      <>
        <PopoverTrigger>{children}</PopoverTrigger>
        <Portal>
          <PopoverContent width={['100%', 350, 500]}>
            <SimpleForm<Data>
              value={data}
              onSubmit={onSubmit}
              field={field}
              onCancel={onClose}
              closeOnSuccess
              submitText={props.submitText}
              theme="detailed"
            />
          </PopoverContent>
        </Portal>
      </>
    )}
  </Popover>
)

const defaultFormBoxProps: BoxProps = {
  // maxH: '100%',
  width: '100%',
  flex: 1,
  flexShrink: 1,
  minH: 0,
}

const footerFlexProps: FlexProps = {
  bg: 'gray.100',
}

const BodyContainer: FC<PropsWithChildren<{ bodyContainerProps?: BoxProps }>> = ({
  children,
  bodyContainerProps,
}) => (
  <Box
    w="100%"
    flex={1}
    py={2}
    px={4}
    minH={0}
    overflowY="auto"
    borderBottom="1px solid"
    borderColor="gray.200"
    {...bodyContainerProps}>
    {children}
  </Box>
)

export const GenericEditModal = <T extends FieldMapValue = FieldMapValue>({
  field,
  data,
  readOnly,
  canSubmitClean,
  adminView,
  submitText,
  corrections,
  onSubmit,
  baseStoragePath,
  formBoxProps,
  closeOnSubmit = true,
  bodyContainerProps,
  subheader,
  inputSize,
  ...props
}: GenericEditModalProps<T>) => {
  const value = useMemo<T>(() => (field && data ? data : ({} as T)), [field, data])
  // close on submit success
  const { height: screenHeight } = useScreen()
  const modalHeight = useMemo(() => Math.min(screenHeight * 0.85, 800), [screenHeight])

  return (
    <DefaultModal
      size="2xl"
      overlayHeader
      closeOnOverlayClick={false}
      contentProps={{ height: 'auto', background: 'gray.50', maxHeight: `${modalHeight}px` }}
      bodyProps={{ height: 'auto', maxHeight: `${modalHeight}px`, overflow: 'hidden' }}
      {...props}
      render={() => {
        if (!field) return <></>
        if (!props.isOpen) return <></>
        if (readOnly || adminView)
          return (
            <Box w="100%" maxH={`${modalHeight}px`} overflowY="auto">
              <BaseDataView
                EditModalComponent={GenericEditModal}
                alwaysExpanded
                childrenExpanded
                adminView={!!adminView}
                corrections={corrections}
                baseStoragePath={baseStoragePath}
                updateField={
                  readOnly
                    ? undefined
                    : (path, data) => {
                        const newValue = {}
                        nestedSet(newValue, path, data)
                        return onSubmit(newValue as T, () => {})
                      }
                }
                onSubmit={
                  readOnly ? undefined : (data, onProgress) => onSubmit(data as T, onProgress)
                }
                field={field}
                data={value}
              />
            </Box>
          )
        return field ? (
          <Flex w="100%" maxH={`${modalHeight}px`} flexDir="column">
            <Box
              py={2}
              px={3}
              bg="gray.100"
              borderBottom="1px solid"
              borderColor="gray.200"
              w="100%">
              <Subheader fontSize="xl">{field.name}</Subheader>
              {subheader ? (
                <Text fontSize="xs" color="gray.500" fontWeight={400} mt={0.5}>
                  {subheader}
                </Text>
              ) : null}
            </Box>
            <SimpleForm<T>
              readOnly={readOnly}
              size={inputSize}
              BodyContainer={BodyContainer}
              bodyContainerProps={bodyContainerProps}
              onSubmit={onSubmit}
              canSubmitClean={canSubmitClean}
              closeOnSuccess={closeOnSubmit}
              footerFlexProps={footerFlexProps}
              onCancel={props.onClose}
              corrections={corrections}
              boxProps={{ ...defaultFormBoxProps, ...formBoxProps }}
              submitText={submitText}
              value={value}
              field={field}
            />
          </Flex>
        ) : (
          <></>
        )
      }}
    />
  )
}
